﻿<script setup lang="ts">
    import { reactive, onMounted, inject, computed } from 'vue';
    import { useRouter } from 'vue-router';
    import List from '@shared/components/List/List.vue';
    import SegmentsListItem from './SegmentsListItem.vue';
    import { SegmentListItem } from './interfaces';
    import { loadSegments, newSegment, deleteSegments } from './services/requests';
    import { setupTags } from '@shared/components/Tags/Tags';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { ContextAccess } from '@shared/Root/Context.access';
    import { bsAlert, AlertTypes } from '@shared/components/BsAlerts';
    import { type ItemReference } from '@shared/components/List/ListTypes';
    import { getWriteAccessDisabledIds } from '@cwd/Acl/useAclAccess';

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const contextAccess: ContextAccess = injectorAccess.injector.get(ContextAccess);

    const router = useRouter();

    const state = reactive<{
        items: SegmentListItem[];
        selected: string[];
        isBusy: boolean;
    }>({
        items: [],
        selected: [],
        isBusy: false,
    });

    const tags = setupTags();

    onMounted(async () => {
        await getSegments();
    });

    const getSegments = async () => {
        state.isBusy = true;

        await loadSegments()
            .then((data: SegmentListItem[]) => {
                state.items = data;
            })
            .finally(() => (state.isBusy = false));
    };

    const createSegment = async () => {
        state.isBusy = true;

        await newSegment()
            .then((segment) => {
                router.push({ name: 'SegmentEdit', params: { segmentId: segment.SegmentId } });
            })
            .finally(() => (state.isBusy = false));
    };

    const filterOptions = () => {
        return ['Name', 'Description'];
    };

    const orderOptions = () => {
        return [
            { display: $Resources.LastModifiedLabel, key: 'Modified' },
            { display: $Resources.NameLabel, key: 'Name' },
        ];
    };

    const onDeleteSelected = () => {
        if (state.isBusy) return;
        state.isBusy = true;

        let list = state.selected.map((id: string) => {
            return {
                SegmentId: id,
            };
        });

        deleteSegments(list)
            .then(() => {
                bsAlert({ title: $Resources.AlertSurveysDeletedHeading, content: $Resources.AlertSegmentsDeletedBody });
                clearSelection();

                getSegments();
            })
            .catch(() => {
                bsAlert({ type: AlertTypes.Danger, title: $Resources.ErrorDeletingSurveysHeading, content: $Resources.ErrorDeletingSegmentsBody });
            })
            .finally(() => {
                state.isBusy = false;
            });
    };

    const clearSelection = () => {
        state.selected = [];
    };

    const aclDisabledIds = computed(() =>
        getWriteAccessDisabledIds(
            state.items,
            (item) => item.SegmentId,
            (item) => item.CurrentUserAccessLevel
        )
    );
</script>

<template>
    <List
        itemId="SegmentId"
        :title="$Resources.SegmentsHeading"
        :description="$Resources.SegmentsDescription"
        :perPage="30"
        :items="state.items"
        :loading="state.isBusy"
        :tags="tags"
        :checkedItems="state.selected"
        :filterKeys="filterOptions()"
        :showCheckbox="(item: ItemReference) => !item.InUse"
        :disabledCheckboxes="aclDisabledIds"
        :orderOptions="orderOptions()"
        @update:checkedItems="(newVal) => (state.selected = newVal as string[])"
        @clearSelected="clearSelection"
        @deleteSelected="onDeleteSelected">
        <template #toolbar>
            <button v-show="state.selected.length === 0" :disabled="state.isBusy" class="btn btn-primary" @click="createSegment">
                {{ $Resources.CreateButton }}
            </button>
        </template>
        <template #item="{ item, index }">
            <SegmentsListItem :segment="item" :index="index" :tags="tags"></SegmentsListItem>
        </template>
    </List>
</template>
